import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import { reducer } from "./reducer";
import { initialState } from "./initialState";
import type { Action, DateFiltersMode, DateFiltersState } from "../types";

export const DateFiltersContext = createContext<DateFiltersState>(initialState);

export interface DateFiltersProviderProps {
    children: ReactNode;
    urlParams?: boolean;
    modes?: DateFiltersMode[];
    mode?: string
}

function computeState({
    urlParams,
    filters,
    modes,
    defaultMode,
}: {
    urlParams: boolean;
    filters: Record<string, any>;
    modes?: DateFiltersMode[];
    defaultMode?: string;
}): DateFiltersState {
    const urlMode = urlParams ? (filters.mode as DateFiltersMode | null) : null;
    const mode = (urlMode && (!modes?.length || modes.includes(urlMode)) ? urlMode : defaultMode) || modes?.[0];

    if (!mode) return { ...initialState, modes: modes || initialState.modes };

    const updatedState: DateFiltersState = {
        ...initialState,
        modes: modes || initialState.modes,
        mode: mode as DateFiltersMode,
    };

    if (["day", "week", "month"].includes(mode)) {
        updatedState.from = moment().startOf(mode as any).format("YYYY-MM-DD");
        updatedState.to = moment().endOf(mode as any).format("YYYY-MM-DD");
    } else if (mode === "custom") {
        updatedState.from = moment().startOf("month").format("YYYY-MM-DD");
        updatedState.to = moment().endOf("month").format("YYYY-MM-DD");
    } else if (mode === "pay-group") {
        updatedState.from = undefined;
        updatedState.to = undefined;
        updatedState.payGroupPeriod = "current";
    }

    if (urlParams) {
        const from = filters.from;
        const to = filters.to;
        if (from && to) {
            updatedState.from = from;
            updatedState.to = to;
        }

        if (mode === "pay-group") {
            const payGroupCode = filters.payGroupCode?.code ?? filters.payGroupCode;
            const payGroupId = filters.payGroupId?.id ?? filters.payGroupId;
            if (payGroupCode) {
                updatedState.payGroupCode = payGroupCode;
                updatedState.payGroupId = payGroupId as any;
            }

            const payGroupPeriod = filters.payGroupPeriod;
            if (payGroupPeriod) {
                updatedState.payGroupPeriod = payGroupPeriod;
            }
        }
    }

    return updatedState;
}

// The search params this state stands for.
function toSearchParams(state: DateFiltersState) {
    const hasRange = Boolean(state?.from && state?.to);
    const isPayGroup = state.mode === "pay-group";

    return {
        mode: state.mode,
        from: hasRange ? state.from : undefined,
        to: hasRange ? state.to : undefined,
        payGroupCode: state?.payGroupCode || undefined,
        payGroupId: state?.payGroupId ? String(state.payGroupId) : undefined,
        payGroupPeriod: isPayGroup && state?.payGroupPeriod ? state.payGroupPeriod : undefined,
    } as Record<string, any>;
}

// Ids come back out of the URL wrapped as objects, so compare them unwrapped.
// Comparing an object against the string it was written as never matches, which
// would keep the state and the URL rewriting each other.
const unwrap = (value: any) =>
    ((value && typeof value === "object" ? value.id ?? value.code ?? value : value) ?? undefined);

function isSameAsUrl(params: Record<string, any>, filters: Record<string, any>) {
    return Object.entries(params).every(([key, value]) => unwrap(filters[key]) === unwrap(value));
}

function DateFiltersProvider({ children, urlParams = true, modes, mode: defaultMode }: DateFiltersProviderProps) {
    const [filters, setFilters] = useFilters();
    const modesKey = modes?.join(",") || "";

    // Actions the user fired that the URL has not caught up with yet. Queueing
    // them keeps handlers that dispatch several actions in a row working.
    const [pending, setPending] = useState<Action[]>([]);
    // Only used when this provider does not live in the URL.
    const [localState, setLocalState] = useState<DateFiltersState>(
        () => computeState({ urlParams, filters, modes, defaultMode })
    );

    // The URL is the source of truth: going back or forward simply re-reads it,
    // so there is no second copy of the state that could drift out of sync.
    const urlState = useMemo(
        () => computeState({ urlParams, filters, modes, defaultMode }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [urlParams, filters, modesKey, defaultMode]
    );

    const base = urlParams ? urlState : localState;

    const state = useMemo(
        () => pending.reduce((acc, action) => reducer(acc, action), base),
        [pending, base]
    );

    const dispatch = useCallback((action: Action) => {
        setPending((prev) => [...prev, action]);
    }, []);

    useEffect(() => {
        if (!urlParams) {
            if (pending.length) {
                setLocalState(state);
                setPending([]);
            }
            return;
        }

        const params = toSearchParams(state);

        if (isSameAsUrl(params, filters)) {
            // The URL says what the state says, so the queue has been applied.
            if (pending.length) setPending([]);
            return;
        }

        // A change the user made here gets its own history entry, so that going
        // back returns to the previous range. Filling in or correcting missing
        // params is not a navigation, so it replaces instead.
        setFilters(params, undefined, { replace: pending.length === 0 });
    }, [state, filters, pending, urlParams, setFilters]);

    const value = useMemo(() => ({ ...state, dispatch }), [state, dispatch]);

    return (
        <DateFiltersContext.Provider value={value}>
            {children}
        </DateFiltersContext.Provider>
    );
}

export default DateFiltersProvider;
